import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const features = [
  {
    icon: "🎓",
    title: "Experienced Trainers",
    description: "Certified and knowledgeable professionals guiding your journey",
    color: "from-sage-400 to-teal-400",
  },
  {
    icon: "🌸",
    title: "Friendly Ambience",
    description: "Positive and motivating atmosphere for your growth",
    color: "from-earth-300 to-earth-400",
  },
  {
    icon: "⭐",
    title: "Best Services",
    description: "Comprehensive wellness programs tailored to your needs",
    color: "from-teal-400 to-lavender-400",
  },
  {
    icon: "🎯",
    title: "Variety of Programs",
    description: "Diverse range of exercises and therapeutic practices",
    color: "from-sage-500 to-sage-600",
  },
];

const WhyUs = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="why-us" className="py-20 lg:py-32 relative overflow-hidden bg-gradient-to-br from-sage-50 via-white to-earth-50">
      {/* Background Decoration */}
      <div className="absolute top-20 left-0 w-72 h-72 bg-sage-200 rounded-full blur-3xl opacity-30"></div>
      <div className="absolute bottom-20 right-0 w-72 h-72 bg-earth-200 rounded-full blur-3xl opacity-30"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Left Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -70 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{
              duration: 0.8,
              ease: "easeInOut",
            }}
            className="flex-1"
          >
            {/* Section Header */}
            <div className="mb-10">
              <span className="inline-block px-4 py-2 bg-sage-100 text-sage-700 rounded-full text-sm font-semibold mb-4">
                Why Choose Us
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Why{" "}
                <span className="gradient-text">Aura Yog Centre</span>?
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Experience the difference with our holistic approach to wellness.
                We combine ancient wisdom with modern practices.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1,
                  }}
                  className="group"
                >
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    {/* Icon */}
                    <div
                      className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <span className="text-3xl">{feature.icon}</span>
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {feature.description}
                    </p>

                    {/* Decorative Line */}
                    <div
                      className={`mt-4 h-1 w-12 bg-gradient-to-r ${feature.color} rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
                    ></div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
              className="mt-10"
            >
              <button
                onClick={() => {
                  const element = document.getElementById("contact");
                  if (element) element.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-primary"
              >
                Join Us Today
              </button>
            </motion.div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{
              duration: 0.8,
              ease: "easeInOut",
            }}
            className="flex-1 relative"
          >
            <div className="relative">
              {/* Main Image */}
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="why.jpg"
                  alt="Yoga Practice at Aura Yog Centre"
                  className="w-full h-auto object-cover"
                />
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-6 -right-6 w-40 h-40 bg-gradient-to-br from-sage-300 to-teal-300 rounded-full blur-2xl opacity-40"></div>
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-gradient-to-br from-earth-300 to-lavender-300 rounded-full blur-2xl opacity-40"></div>

              {/* Floating Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.8 }}
                className="absolute -bottom-8 -left-8 bg-white rounded-2xl p-6 shadow-2xl hidden lg:block"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sage-400 to-sage-500 flex items-center justify-center">
                    <span className="text-2xl">🏆</span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold gradient-text">5+ Years</div>
                    <div className="text-sm text-gray-600">Experience</div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Rating Card */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 1 }}
                className="absolute -top-8 -right-8 bg-white rounded-2xl p-6 shadow-2xl hidden lg:block"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-earth-400 to-earth-500 flex items-center justify-center">
                    <span className="text-2xl">⭐</span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold gradient-text">5.0</div>
                    <div className="text-sm text-gray-600">Rating</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
